/**
 * Toast queue — module-level singleton matching process-runner's
 * `toastQueue.add(config, options)` call signature so login error
 * patterns transfer 1:1. Subscribed `<ToastContainer />` re-renders
 * whenever the list changes.
 */

export type ToastVariant = 'success' | 'error' | 'info' | 'warning'

export interface ToastConfig {
  message: string
  variant?: ToastVariant
  title?: string
}

export interface ToastOptions {
  /** Auto-dismiss delay in ms. `0` keeps the toast until manually closed. Defaults to 5000. */
  timeout?: number
}

export interface ToastItem extends ToastConfig {
  id: number
  timeout: number
}

type Listener = (items: ToastItem[]) => void

let nextId = 1
let items: ToastItem[] = []
const listeners = new Set<Listener>()

function emit() {
  for (const l of listeners) l(items)
}

export const toastQueue = {
  add(config: ToastConfig, options: ToastOptions = {}): number {
    const id = nextId++
    const timeout = options.timeout ?? 5000
    const item: ToastItem = {
      id,
      message: config.message,
      title: config.title,
      variant: config.variant ?? 'info',
      timeout,
    }
    items = [...items, item]
    emit()
    if (timeout > 0) {
      setTimeout(() => toastQueue.dismiss(id), timeout)
    }
    return id
  },

  dismiss(id: number): void {
    if (!items.some((t) => t.id === id)) return
    items = items.filter((t) => t.id !== id)
    emit()
  },

  clear(): void {
    items = []
    emit()
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    listener(items)
    return () => listeners.delete(listener)
  },
}
